import { Form, Formik } from 'formik';
import * as bd from 'react-basic-design';
import { TutorialDTO } from '../../../api/tutorial-api';
import SvgClose from '../../../assets/icons/Close';
import SvgDelete from '../../../assets/icons/Delete';
import SvgSave from '../../../assets/icons/Save';
import { BasicModal } from '../../../components/basic/basic-modal';
import { T } from '../../../components/basic/text';
import { yup } from '../../../components/basic/yup';
import { FormikCellToggle, FormikTextField, FormikTinyEditor } from '../../../components/formik';

interface SaveTutorialModalProps {
    show: boolean;
    allowEdit: boolean;
    selectedTutorial?: TutorialDTO;
    products: Array<{ id: number; name: string }>;
    onHide: () => void;
    onSave: (values: TutorialDTO) => void;
    onDelete?: (tutorial: TutorialDTO) => void;
}

export function SaveTutorialModal({
    show,
    allowEdit,
    selectedTutorial,
    products,
    onHide,
    onSave,
    onDelete,
}: SaveTutorialModalProps) {
    const TutorialSchema = yup.object().shape({
        title: yup.string().required(' '),
        youtubeVideoId: yup.string().required(' '),
        order: yup.number().required(' ').min(0),
    });

    const defaultValues: TutorialDTO = {
        id: selectedTutorial?.id,
        title: selectedTutorial ? selectedTutorial.title : '',
        description: selectedTutorial ? selectedTutorial.description : '',
        youtubeVideoId: selectedTutorial ? selectedTutorial.youtubeVideoId : '',
        category: selectedTutorial?.category || '',
        productId: selectedTutorial?.productId,
        duration: selectedTutorial?.duration,
        order: selectedTutorial?.order || 0,
        isPublished: selectedTutorial?.isPublished || false,
    };

    const insertMode = !selectedTutorial;

    return (
        <BasicModal
            size="xl"
            show={show}
            onHide={onHide}
            title={insertMode ? <T>new-tutorial</T> : <T>edit-tutorial</T>}
        >
            <Formik
                initialValues={defaultValues}
                enableReinitialize
                validationSchema={TutorialSchema}
                onSubmit={(values) => {
                    console.log('Form values on submit:', values);
                    onSave(values);
                }}
            >
                {formik => (
                    <Form>
                        <div className="p-3">
                            <bd.Flex gap={3} wrap className="mb-3">
                                <FormikTextField
                                    name="title"
                                    label={<T>title</T>}
                                    disabled={!allowEdit}
                                    required
                                    width="100%"
                                />
                            </bd.Flex>

                            <div className="mb-3">
                                <label className="form-label">
                                    <T>description</T>
                                </label>
                                <FormikTinyEditor
                                    name="description"
                                    content={selectedTutorial?.description || ''}
                                    readOnly={!allowEdit}
                                    height={400}
                                />
                            </div>

                            <bd.Flex gap={3} wrap className="mb-3">
                                <FormikTextField
                                    name="youtubeVideoId"
                                    label={<T>youtube-video-id</T>}
                                    disabled={!allowEdit}
                                    required
                                    width="calc(50% - 1.5rem)"
                                />

                                <FormikTextField
                                    name="category"
                                    label={<T>category</T>}
                                    disabled={!allowEdit}
                                    width="calc(50% - 1.5rem)"
                                />
                            </bd.Flex>

                            <div className="mb-3">
                                <small className="text-muted">
                                    <T>youtube-id-helper</T>
                                </small>
                            </div>

                            <bd.Flex gap={3} wrap className="mb-3">
                                <FormikTextField
                                    name="order"
                                    label={<T>display-order</T>}
                                    type="number"
                                    disabled={!allowEdit}
                                    required
                                    width="calc(25% - 1.5rem)"
                                />

                                <FormikTextField
                                    name="duration"
                                    label={<T>duration-seconds</T>}
                                    type="number"
                                    disabled={!allowEdit}
                                    width="calc(25% - 1.5rem)"
                                />
                            </bd.Flex>

                            <bd.Flex gap={3} wrap className="mb-3">
                                <FormikCellToggle
                                    name="isPublished"
                                    label={<T>published</T>}
                                    disabled={!allowEdit}
                                />
                            </bd.Flex>

                            {/* YouTube Preview */}
                            {formik.values.youtubeVideoId && (
                                <div className="mt-3">
                                    <label className="form-label">
                                        <T>video-preview</T>
                                    </label>
                                    <div className="ratio ratio-16x9">
                                        <iframe
                                            src={`https://www.youtube.com/embed/${formik.values.youtubeVideoId}`}
                                            title="YouTube video preview"
                                            allowFullScreen
                                        ></iframe>
                                    </div>
                                </div>
                            )}
                        </div>

                        <bd.Flex gap={2} content="end" className="p-3 border-top">
                            <bd.Button
                                type="button"
                                variant="outline"
                                size="sm"
                                icon={<SvgClose />}
                                onClick={onHide}
                            >
                                <T>close</T>
                            </bd.Button>
                            {!insertMode && allowEdit && onDelete && (
                                <bd.Button
                                    type="button"
                                    variant="outline"
                                    color="secondary"
                                    size="sm"
                                    icon={<SvgDelete />}
                                    onClick={() => onDelete(selectedTutorial!)}
                                >
                                    <T>delete</T>
                                </bd.Button>
                            )}
                            {allowEdit && (
                                <>
                                    <bd.Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => console.log('Current formik values:', formik.values)}
                                    >
                                        Debug
                                    </bd.Button>
                                    <bd.Button
                                        type="submit"
                                        variant="contained"
                                        size="sm"
                                        icon={<SvgSave />}
                                        disabled={!formik.isValid}
                                    >
                                        <T>save</T>
                                    </bd.Button>
                                </>
                            )}
                        </bd.Flex>
                    </Form>
                )}
            </Formik>
        </BasicModal>
    );
}
